import atob from "atob"
import * as R from "ramda"

export const isNilOrEmpty = (a: any): any => R.isNil(a) || R.isEmpty(a)

export const getCookie = (name: string) => {
  const match = document.cookie.match(new RegExp("(^| )" + name + "=([^;]+)"))
  return match ? decodeURIComponent(match[2]) : null
}

// Safely get nested prop
export const getIn = (o: object, path: string | string[], defaultValue?: any) =>
  (o &&
    path &&
    R.view(R.lensPath(typeof path === "string" ? path.split(".") : path))(o)) ||
  defaultValue

const getDiscount = (compareAtPrice, price) => {
  if (compareAtPrice === price || price > compareAtPrice) {
    return -1
  }

  const discountPercentage = (compareAtPrice - price) / compareAtPrice
  return Math.floor(discountPercentage / 0.05)
}

export const hasVariants = (p: {
  variants?: unknown
}): p is { variants: any[] } => {
  return Array.isArray((p as any).variants) && (p as any).variants.length > 0
}

export const formatContentfulVariant = (contentfulVariant) => ({
  ...R.pick([
    "availability",
    "sku",
    "upc",
    "price",
    "compareAtPrice",
    "marketPrices",
    "name",
    "language",
    "specs",
    "specSheets",
    "outOfStockMessage",
    "resourcesText",
    "resourcesMedia",
    "showProp65Warning",
  ])(contentfulVariant),
  color: getIn(contentfulVariant, "color.name"),
  contentfulVariantId: getIn(contentfulVariant, "contentful_id"),
  deviceBrandName: getIn(contentfulVariant, "device.brand.name"),
  deviceBrandSlug: getIn(contentfulVariant, "device.brand.slug"),
  deviceName: getIn(contentfulVariant, "device.name"),
  deviceSlug: getIn(contentfulVariant, "device.slug"),
  discount: getIn(contentfulVariant, "compareAtPrice")
    ? getDiscount(
        getIn(contentfulVariant, "compareAtPrice"),
        getIn(contentfulVariant, "price")
      )
    : -1,
  hexString: getIn(contentfulVariant, "color.hexString"),
  images: getIn(contentfulVariant, "images", []).map(
    (image) => image?.file?.url
  ),
  size: getIn(contentfulVariant, "size.size"),
  swatchImage: getIn(contentfulVariant, "color.swatchImage.file.url"),
  variantId: getIn(contentfulVariant, "shopifyVariantId"),
})

export const getDecodedShopifyProductId = (productId) => {
  let decodedProductId
  try {
    decodedProductId = atob(productId)
  } catch {
    decodedProductId = productId
  }

  return parseInt(R.replace("gid://shopify/Product/", "")(decodedProductId))
}

export const getDecodedShopifyProductIdString = (productId: string) => {
  let decodedProductId
  try {
    decodedProductId = atob(productId)
  } catch {
    decodedProductId = productId
  }

  return R.replace("gid://shopify/Product/", "")(decodedProductId)
}

const NOT_CORRECTLY_ENCODED_ERROR =
  "Failed to execute 'atob' on 'Window': The string to be decoded is not correctly encoded."
export const getDecodedShopifyCustomerId = (customerId) => {
  let decodedCustomerId

  try {
    decodedCustomerId = atob(customerId)
  } catch (error) {
    if (error.message === NOT_CORRECTLY_ENCODED_ERROR) {
      decodedCustomerId = customerId
    } else {
      decodedCustomerId = ""
    }
  }

  return R.replace("gid://shopify/Customer/", "")(decodedCustomerId)
}

export const getCollectionBannerData = (data) => {
  return {
    bannerHeader: data?.bannerHeader,
    bannerImage: data?.bannerImage?.file?.url || "",
    bannerSubheader: data?.bannerSubheader?.bannerSubheader,
    collectionBannerType: data?.collectionBannerType,
    mobileBannerImage:
      data?.mobileBannerImage?.file?.url || data?.bannerImage?.file?.url || "",
    paragraphAsymmetricalSplit: !!data?.paragraphAsymmetricalSplit,
    paragraphBody: data?.paragraphBody?.paragraphBody,
    paragraphHeader: data?.paragraphHeader,
  }
}

export const pluralizeDeviceType = (deviceTypeLabel) => {
  if (!deviceTypeLabel) {
    return ""
  }

  const lastTwoLetters = deviceTypeLabel.slice(deviceTypeLabel.length - 2)
  const lastLetter = deviceTypeLabel[deviceTypeLabel.length - 1]

  if (lastTwoLetters === "ch") {
    return `${deviceTypeLabel}es`
  } else if (lastLetter === "s") {
    return deviceTypeLabel
  } else {
    return `${deviceTypeLabel}s`
  }
}
